import React, { memo, useEffect, useRef, useState } from 'react';
import { Stack, Image, Button } from 'react-bootstrap';
import { S3_URL, USER_TYPES } from 'constants/index';
import { IMAGE_PATHS } from 'constants/imagePaths';
import { useNavigate } from 'react-router-dom';
import parse from 'html-react-parser';
import { sanitizeHtml } from 'components/sanitizeHtml';
import {
	useGetCheckSubscriptionMutation,
	useAddToTalentMutation,
	useRemoveConnectRequestMutation,
	useSendConnectRequestMutation,
} from 'services';
import SaveTalentBtn from '../ActionButton/SaveTalent';
import { useQueryClient } from '@tanstack/react-query';
import useTranslate from 'hooks/useTranslate';
import useCommonStore, { setModalConfig } from 'store/common';
import useAuthStore from 'store/auth';
import ChatBtn from '../ActionButton/ChatBtn';
import './styles.scss';

const status: any = {
	0: '+ Connect',
	1: 'Connected',
	2: 'Requested',
};

const CandidateDetailBox: React.FC<any> = ({
	applicantList,
	jobId,
	actions,
	aboutUs = true,
	jobTitle = '',
	refetch = () => {},
}) => {
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const { translate } = useTranslate();
	const descriptionRef = useRef(null);
	const { hideCommonModal }: any = useCommonStore((state) => state);
	const [hasMore, setHasMore] = useState(false);
	const [isFavourite, setIsFavourite] = useState<boolean>(applicantList?.isSaved);
	const { mutateAsync: addToFavMutation } = useAddToTalentMutation();
	const { userType = '', companyName = '' }: any = useAuthStore((state) => state?.userInfo?.user);
	const [isConnected, setIsConnected] = useState(status[applicantList?.isConnected]);
	const { mutateAsync: sendRequestMutation } = useSendConnectRequestMutation();
	const { mutateAsync: removeConnectionMutation } = useRemoveConnectRequestMutation();
	const { mutateAsync: checkSubscriptionMutation } = useGetCheckSubscriptionMutation();

	useEffect(() => {
		const element: any = descriptionRef?.current;
		if (element?.scrollHeight > element?.clientHeight) {
			setHasMore(true);
		}
	}, [applicantList?.about]);

	const onScheduleMeetingClick = () => {
		checkSubscriptionMutation('schedule-meeting').then((res) => {
			if (res.success) {
				setModalConfig({
					visible: true,
					id: applicantList?.userId,
					type: 'meetingScheduleModalCompany',
					data: {
						userName: applicantList?.name,
						profilePicture: applicantList?.profilePicture,
						jobId,
						seekerDesc: `${applicantList?.designation} at ${applicantList?.companyName}`,
						companyDesc: `${jobTitle} with ${companyName}`,
					},
					props: {
						dialogClassName: 'schedule-meeting-dialog',
					},
				});
			} else {
				setModalConfig({
					visible: true,
					type: 'subscriptionAlert',
					onClose: () => {
						hideCommonModal();
					},
					onClick: () => {
						navigate('/company/subscriptions');
						hideCommonModal();
					},
				});
			}
		});
	};

	const addToFavorite = () => {
		setIsFavourite((prev) => !prev);
		addToFavMutation({ userId: applicantList.userId })
			.then((_res: any) => {
				queryClient.invalidateQueries({ queryKey: ['get-saved-talent'] });
			})
			.catch(() => {
				setIsFavourite((prev) => !prev);
			});
	};

	const onCardClick = () => {
		navigate(`/seeker/profile/${applicantList?.userId}`);
	};

	const handleConnectRequest = (id: any) => {
		if (['+ Connect'].includes(isConnected)) {
			sendConnectionRequest(id);
		} else if (['Connected'].includes(isConnected)) {
			removeConnectionModal(id);
		}
	};

	const sendConnectionRequest = (id: any) => {
		setIsConnected('Requested');
		sendRequestMutation({ id: Number(id) })
			.then((res: any) => {
				if (res?.success) {
					//
				} else {
					setIsConnected('+ Connect');
				}
			})
			.catch((_err: any) => {
				setIsConnected('+ Connect');
			});
	};

	const removeConnection = (id: any) => {
		setIsConnected('+ Connect');
		removeConnectionMutation(id)
			.then((res: any) => {
				if (res?.success) {
					setModalConfig({
						visible: false,
					});
				} else {
					setIsConnected('Connected');
				}
			})
			.catch((_err: any) => {
				setIsConnected('Connected');
			});
	};

	const removeConnectionModal = (id: any) => {
		setModalConfig({
			visible: true,
			type: 'confirmModal',
			id,
			onClick: removeConnection,
			data: {
				title: translate('Modal.wantToRemoveConnection'),
			},
			props: {
				dialogClassName: 'confirm-modal',
			},
		});
	};

	return (
		<Stack className="applicants-list-box h-100" onClick={() => onCardClick()}>
			<div className="d-flex position-relative">
				<div className="applicants-info d-flex flex-column flex-sm-row align-items-sm-center gap-2 gap-md-3 flex-1">
					<div className="applicants-img rounded-circle">
						<Image
							src={`${S3_URL}${applicantList.profilePicture}`}
							onError={({ currentTarget }) => {
								currentTarget.onerror = null;
								currentTarget.src = IMAGE_PATHS.UserDefault;
							}}
							className="w-100 h-100 object-fit-cover rounded-circle"
							loading="lazy"
						/>
					</div>
					<Stack className="gap-1 justify-content-center">
						{applicantList?.name && <h4 className="fw-bold mb-0">{applicantList.name}</h4>}
						{applicantList?.designation && <p className="mb-0">{applicantList.designation}</p>}
						{applicantList?.companyName && <p className="mb-0">{applicantList?.companyName}</p>}
					</Stack>
				</div>
				<div className="applicants-info-actions d-flex gap-2 gap-md-3">
					{actions && (
						<ChatBtn
							className="edit-btn p-0 rounded-circle"
							receiverId={applicantList?.userId}
							mode="icon"
						/>
					)}
					{userType === USER_TYPES.COMPANY ? (
						<SaveTalentBtn
							isFavourite={isFavourite}
							handleClick={(e: any) => {
								e.stopPropagation();
								addToFavorite();
							}}
						/>
					) : (
						<div>
							{applicantList?.isRequested === 1 ? (
								<Button
									disabled
									onClick={(e) => {
										e.stopPropagation();
									}}
									className="filled-btn rounded-pill lh-1"
								>
									{translate('Common.requested')}
								</Button>
							) : (
								<Button
									onClick={(e) => {
										e.stopPropagation();
										handleConnectRequest(applicantList?.userId);
									}}
									className="filled-btn rounded-pill lh-1"
								>
									{isConnected}
								</Button>
							)}
						</div>
					)}
				</div>
			</div>
			{aboutUs && applicantList?.about?.length > 0 && (
				<div className="applicants-description">
					<p ref={descriptionRef} className={`description-truncated ${hasMore ? 'has-more' : ''}`}>
						{parse(sanitizeHtml(applicantList.about))}
					</p>
				</div>
			)}
			<div className="applicants-about d-flex flex-column flex-sm-row">
				<div className="d-flex gap-1">
					<i className="ri-briefcase-line"></i>
					<p className="mb-0 fw-bold">
						{applicantList?.totalExperience > 0
							? `${applicantList?.totalExperience} yrs`
							: translate('Common.fresher')}
					</p>
				</div>
				<div className="d-flex gap-1">
					<i className="ri-map-pin-line"></i>
					<p className="mb-0 fw-bold">{applicantList?.address}</p>
				</div>
			</div>
			{applicantList?.skills?.length > 0 && (
				<div className="applicants-skills-list d-flex flex-wrap gap-2 gap-sm-3">
					{applicantList?.skills?.map((skill: any) => (
						<div key={`${skill?.id}-${skill?.title}`} className="skill-box rounded-pill">
							{skill?.title}
						</div>
					))}
				</div>
			)}
			{actions && (
				<div className="applicants-actions d-flex gap-2 gap-md-3 flex-column flex-sm-row mt-auto">
					<Button
						className="gray-transparent-btn rounded-pill"
						onClick={(e: any) => {
							e.stopPropagation();
							setModalConfig({
								visible: true,
								id: applicantList?.id,
								type: 'rejectModal',
								refetch: refetch,
							});
						}}
					>
						{translate('Common.reject')}
					</Button>
					{applicantList?.isAssessment > 0 && (
						<Button
							onClick={(e: any) => {
								e.stopPropagation();
								navigate(`/company/applicant-assessment/${jobId}/${applicantList?.userId}`);
							}}
							className="transparent-btn rounded-pill"
						>
							{translate('Questionnaire.viewQuestionnaire')}
						</Button>
					)}
					<Button
						onClick={(e: any) => {
							e.stopPropagation();
							onScheduleMeetingClick();
						}}
						className="skilled-btn rounded-pill"
					>
						{translate('Interview.scheduleInterview')}
					</Button>
				</div>
			)}
		</Stack>
	);
};
export default memo(CandidateDetailBox);
